import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { canOpenInBench, canRerun, canReview, costLabel, type HistoryItemWire } from "../api/history";
import { requestGeneration } from "../api/ringswap";
import { submitReview } from "../api/reviews";
import { useAuth } from "../auth/AuthProvider";
import { BeforeAfter } from "../bench/BeforeAfter";
import { toCurl } from "../bench/curl";
import { metalDisplay } from "../bench/enums";
import { toDisplayUrl } from "../bench/mapping";
import { POLL_MAX_TRIES, pollToFinal } from "../bench/poll";
import { config } from "../config";
import type { BenchPrefill } from "./prefill";

/** Full gateway URL for the generations endpoint — rebuilt locally from config (never imported from
 * the bench) so the drawer's cURL/re-run target stays correct if the bench moves. */
const GENERATIONS_URL = `${config.apiBase}${config.ringSwapPrefix}/v1/generations`;

/** Join truthy class names. */
const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleString("en-GB") : "—");

/** A single fact row in the drawer's <dl>. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** Copyable job reference — mirrors the bench's JobRef so testers cross-reference job ids. */
function JobRef({ jobId }: { jobId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard.writeText(jobId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div className="lab-job">
      <span className="muted">job</span>
      <span className="id" title={jobId}>
        {jobId}
      </span>
      <button className="btn small-btn" type="button" onClick={copy}>
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

/**
 * Detail drawer for one generation-history row. Overlay + right-hand panel; closes on backdrop
 * click or Escape. Renders the before/after (or single plate), the request facts, the replayable
 * payload, and gated actions (Open-in-bench / Re-run / Approve-Reject). Gated actions are rendered
 * DISABLED (not hidden) when the row predates request snapshots, so the affordance is always visible.
 */
export function HistoryDrawer({ item, onClose, onChanged }: { item: HistoryItemWire; onClose: () => void; onChanged: () => void }) {
  const { service } = useAuth();
  const navigate = useNavigate();

  // Re-run state (guarded against double-fire; never auto-fires).
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Review state.
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Copy affordance for the payload block.
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (what: string, text: string): void => {
    void navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 1200);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const before = toDisplayUrl(item.source_image_url);
  const after = toDisplayUrl(item.output_image_url ?? undefined);
  const singleImage = after ?? before; // fallback plate when we lack both

  const metalTag = item.target_metal ? metalDisplay(item.target_metal) : null;

  const openInBench = (): void => {
    const rj = item.request_json;
    if (!rj) return;
    const prefill: BenchPrefill = {
      ring_sku: rj.ring_sku,
      ...(rj.target_metal ? { target_metal: rj.target_metal } : {}),
      ...(rj.ring_image_id ? { ring_image_id: rj.ring_image_id } : {}),
      ...(rj.stone_sku
        ? {
            stone: {
              id: rj.stone_sku,
              name: item.stone?.type ?? rj.stone_sku,
              thumbnailUrl: item.stone?.reference_image_url ?? null,
              stone: {
                ...(item.stone?.type ? { type: item.stone.type } : {}),
                ...(item.stone?.shape ? { shape: item.stone.shape } : {}),
              },
              // A history record keeps only the one reference photo, not the product's gallery.
              imageUrls: item.stone?.reference_image_url ? [item.stone.reference_image_url] : [],
            },
          }
        : {}),
    };
    navigate("/bench", { state: { prefill } });
  };

  const rerun = async (): Promise<void> => {
    const rj = item.request_json;
    if (!rj || running) return;
    setRunning(true);
    setRunError(null);
    setRunStatus("submitting…");
    try {
      const gen = await requestGeneration(service, rj);
      if (gen.httpStatus >= 400) {
        setRunError(`HTTP ${gen.httpStatus}: ${gen.error ?? "request failed"}`);
        return;
      }
      if (gen.status === "complete" || gen.status === "noop") {
        setRunStatus(`cache hit — unchanged at cfg v${item.generation_config_version}`);
        onChanged();
        return;
      }
      if (gen.status === "rejected") {
        setRunStatus("rejected");
        onChanged();
        return;
      }
      if (!gen.jobId) {
        setRunError("accepted but no job_id returned");
        return;
      }
      setRunStatus(`accepted · job ${gen.jobId.slice(0, 8)}…`);
      const job = await pollToFinal(service, gen.jobId, (a, s) => setRunStatus(`poll ${a}/${POLL_MAX_TRIES} · ${s}s`));
      // "timeout" only means we stopped watching — the job is still running and will land here.
      setRunStatus(job.status === "timeout" ? "still running — stopped watching; refresh History shortly" : `terminal · ${job.status}`);
      onChanged();
    } catch (e) {
      setRunError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const review = async (decision: "approve" | "reject"): Promise<void> => {
    if (reviewing) return;
    setReviewing(true);
    setReviewError(null);
    try {
      await submitReview(service, item.id, decision);
      onChanged();
      onClose();
    } catch (e) {
      setReviewError((e as Error).message);
      setReviewing(false); // keep the drawer open so the error is readable
    }
  };

  const curl = item.request_json ? toCurl(GENERATIONS_URL, item.request_json) : null;
  const payloadJson = item.request_json ? JSON.stringify(item.request_json, null, 2) : null;

  // Dialog: Escape + backdrop close, focusable Close button, aria-labelledby the SKU. Focus
  // trap/restore is deliberately omitted (internal admin tool; Tab + Escape suffice).
  return (
    <div className="lab-overlay" role="dialog" aria-modal="true" aria-labelledby="hist-drawer-sku" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lab-drawerpanel">
        <div className="lab-drawerhead">
          <div className="who">
            <div className="sku" id="hist-drawer-sku">{item.ring_sku}</div>
            <div className="op">{item.operation}</div>
          </div>
          <button className="btn small-btn x" type="button" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>

        {before && after ? (
          <BeforeAfter before={before} after={after} />
        ) : singleImage ? (
          <div className="lab-plate">
            <img className="hero" src={singleImage} alt={item.ring_sku} />
            <span className="tick tl" />
            <span className="tick tr" />
            <span className="tick bl" />
            <span className="tick br" />
          </div>
        ) : (
          <div className="lab-plate">
            <span className="no-image">no image</span>
          </div>
        )}

        <dl className="lab-facts">
          <Fact label="status">
            <span className={`s-${item.status}`}>{item.status}</span>
          </Fact>
          <Fact label="qa">
            {item.qa_status ?? "—"}
            {item.qa_score !== null ? ` · ${item.qa_score}` : ""}
          </Fact>
          <Fact label="operation">{item.operation}</Fact>
          {metalTag && (
            <Fact label="metal">
              <span className="tag">
                <span className={cx("sw", "bead", metalTag.swatchClass)} />
                {metalTag.family} {metalTag.karat}
              </span>
            </Fact>
          )}
          {item.stone && (
            <Fact label="stone">
              {item.stone.type}
              {item.stone.shape ? ` · ${item.stone.shape}` : ""}
              {item.stone.carat_band ? ` · ${item.stone.carat_band}` : ""}
            </Fact>
          )}
          <Fact label="model">
            {item.model ?? "—"}
            {item.model_version ? ` · ${item.model_version}` : ""}
          </Fact>
          <Fact label="config">cfg v{item.generation_config_version}</Fact>
          <Fact label="cost">{costLabel(item.cost_micros)}</Fact>
          <Fact label="attempts">{item.attempt_count}</Fact>
          {item.reviewed_by && <Fact label="reviewed by">{item.reviewed_by}</Fact>}
          <Fact label="created">{fmt(item.created_at)}</Fact>
          <Fact label="completed">{fmt(item.completed_at)}</Fact>
        </dl>

        {payloadJson && (
          <div className="lab-payload">
            <div className="lab-divlabel">request</div>
            <pre className="json">{payloadJson}</pre>
            <div className="lab-copyrow">
              <button className="btn small-btn" type="button" onClick={() => copy("payload", payloadJson)}>
                {copied === "payload" ? "Copied!" : "Copy payload"}
              </button>
              {curl && (
                <button className="btn small-btn" type="button" onClick={() => copy("curl", curl)}>
                  {copied === "curl" ? "Copied!" : "Copy as cURL"}
                </button>
              )}
            </div>
          </div>
        )}

        <JobRef jobId={item.id} />

        <div className="lab-actrow">
          <button
            className="btn"
            type="button"
            disabled={!canOpenInBench(item)}
            title={canOpenInBench(item) ? undefined : "recorded before request snapshots"}
            onClick={openInBench}
          >
            Open in Test Bench
          </button>
          <button
            className="btn"
            type="button"
            disabled={!canRerun(item) || running}
            title={canRerun(item) ? undefined : "recorded before request snapshots"}
            onClick={() => void rerun()}
          >
            {running ? "Re-running…" : "Re-run"}
          </button>
          {canReview(item) && (
            <>
              <button className="btn approve" type="button" disabled={reviewing} onClick={() => void review("approve")}>
                Approve
              </button>
              <button className="btn reject" type="button" disabled={reviewing} onClick={() => void review("reject")}>
                Reject
              </button>
            </>
          )}
        </div>

        {runStatus && !runError && <div className="lab-statusline">{runStatus}</div>}
        {runError && <div className="error small" style={{ marginTop: "0.5rem" }}>✕ {runError}</div>}
        {reviewError && <div className="error small" style={{ marginTop: "0.5rem" }}>✕ {reviewError}</div>}
      </div>
    </div>
  );
}
